//Library Imports
import styled from "styled-components";
import store from "store";
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";

//Local Imports
import { ReactComponent as ReloadIcon } from "../../../../assets/img/icons/reload.svg";
import { ReactComponent as SearchBulb } from "../../../../assets/img/icons/SearchBulb.svg";
import { ReactComponent as SearchCross } from "../../../../assets/img/icons/SearchCross.svg";
import SearchTermsContainer from "./SearchTermsContainer";
import SearchTermExtended from "./SearchTermExtended";
import { PREVIEW_ARTICLE, SEARCH_ARTICLES } from "constants/actionTypes";
import { removeSelectedIds } from "modules/Content/DragnDrop/actions";
import { GLOBAL_ACTION } from "modules/GlobalActions";
import { ASSET_DETAILS } from "constants/types";
import SearchAdvance from "../../../../assets/img/icons/SearchAdvance.svg";
import { getSearchArticles } from "../actions";

const Search = (props) => {
  const searchNode = useRef();
  const searchInputContainer = useRef();
  const {
    onSearch,
    onEdit,
    editable,
    smartSearchTerms,
    hideFocusMessage,
    noKeywords,
    isAssetWithoutCampaign,
    savedKeywords,
    hideUpdateButton,
    sortBy,
    dateFilter,
    setSelectedDatesObj,
    favourites,
  } = props;
  const history = useHistory();

  const [searchTerms, setSearchTerms] = useState([]);
  const [extendedSearchTerms, setExtendedSearchTerms] = useState([]);
  const [lowerCasedSearchTermsState, setLowerCasedSearchTermsState] = useState([]);
  const [lowerCasedExtendedSearchTermsState, setLowerCasedExtendedSearchTermsState] = useState([]);
  const [lowerCasedSavedKeywordsState, setLowerCasedSavedKeywordsState] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [initiallyAdded, setInitiallyAdded] = useState(false);

  const advancedSearchCheck = useSelector((state) => state.uiReducer.advancedSearchCheck);
  const sourcedContent = useSelector((state) => state.searchArticles.sourcedContent);
  const excludedContent = useSelector((state) => state.searchArticles.excludedContent);
  const excludedKeywords = useSelector((state) => state.searchArticles.excludedKeywords);
  const includedKeywords = useSelector((state) => state.searchArticles.includedKeywords);

  useEffect(() => {
    const lowerCasedSavedKeywords = savedKeywords?.map((term) => term.toLowerCase());
    setLowerCasedSavedKeywordsState(lowerCasedSavedKeywords);
  }, [savedKeywords]);

  useEffect(() => {
    const lowerCasedSearchTerms = searchTerms?.map((term) => term.toLowerCase());
    setLowerCasedSearchTermsState(lowerCasedSearchTerms);
  }, [searchTerms]);

  useEffect(() => {
    if (advancedSearchCheck) {
      setSearchTerms([]);
      setExtendedSearchTerms([]);
      setSearchValue("");
    }
  }, [advancedSearchCheck]);

  useEffect(() => {
    const lowerCasedExtendedSearchTerms = extendedSearchTerms?.map((term) => term.toLowerCase());
    setLowerCasedExtendedSearchTermsState(lowerCasedExtendedSearchTerms);
  }, [extendedSearchTerms]);

  let crossIcon = document.getElementsByClassName("clear-item")[0];
  let searchIcon = document.getElementsByClassName("search-icon")[0];

  // common util to split seasrch terms and populate extended/main search field
  const updateSearchTerms = (currentSearchTerms = []) => {
    // set search terms count based on characters
    //convert all to small case
    currentSearchTerms = currentSearchTerms.map((v) => v.toLowerCase().replace(" and ", " AND "));
    // add only unique terms
    currentSearchTerms = [...new Set(currentSearchTerms)];
    store.dispatch({
      // This action will update the searched terms in the store that are to be highlighted in the article preview sidebar
      type: PREVIEW_ARTICLE.UPDATE_SEARCH_TERMS,
      payload: currentSearchTerms,
    });

    let searchTermsSplitter = 100;
    let charactersLimit = 45;
    let searchElementWidth = searchNode.current.offsetWidth;
    if (searchElementWidth <= 1000) {
      charactersLimit = 25;
    }

    let fontSize = getCanvasFontSize(searchNode.current);

    let searchIconWidth = 16 + 16;
    if (searchIcon) {
      let style = window.getComputedStyle(searchIcon);
      let mL = getNumericValue(style.marginLeft, 10);
      let mR = getNumericValue(style.marginRight, 10);
      let pL = getNumericValue(style.paddingLeft, 10);
      let pR = getNumericValue(style.paddingRight, 10);
      searchIconWidth = searchIcon.offsetWidth + mL + mR + pL + pR;
    } else {
      searchIcon = document.getElementsByClassName("search-icon")[0];
    }

    let crossIconWidth = 12 + 12;
    if (crossIcon) {
      let style = window.getComputedStyle(crossIcon);
      let mL = getNumericValue(style.marginLeft, 10);
      let mR = getNumericValue(style.marginRight, 10);
      let pL = getNumericValue(style.paddingLeft, 10);
      let pR = getNumericValue(style.paddingRight, 10);
      crossIconWidth = crossIcon.offsetWidth + mL + mR + pL + pR;
    } else {
      crossIcon = document.getElementsByClassName("clear-item")[0];
    }

    let iconsWidth = searchIconWidth + crossIconWidth;

    const wholeWidth = searchInputContainer.current.offsetWidth;
    let sixityPercentWholeWidth = wholeWidth * 0.6; //60% of width
    let occupiedWidth = 0;
    currentSearchTerms.every((element, index) => {
      let widthOfWords = getTextWidth(element, fontSize);
      occupiedWidth += widthOfWords + iconsWidth + 7;
      if (occupiedWidth > sixityPercentWholeWidth) {
        searchTermsSplitter = index;
        return false;
      }
      return true;
    });

    // show cross on search if search terms exists
    setShowReset(currentSearchTerms.length > 0);
    if (currentSearchTerms.length === 0) {
      resetSearch();
    } else if (currentSearchTerms.length < searchTermsSplitter) {
      setSearchTerms(currentSearchTerms);
      setExtendedSearchTerms([]);
    } else {
      setSearchTerms(currentSearchTerms.slice(0, searchTermsSplitter));
      setExtendedSearchTerms(currentSearchTerms.slice(searchTermsSplitter, currentSearchTerms.length));
    }
    // if (!currentSearchTerms.length > 0 && )
  };

  // on , or enter click add search terms to current array and update article results
  const addSearchTerm = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      let searchText = event.target.value;
      const currentSearchTerms = searchText
        .toLowerCase()
        .trim()
        .split(",")
        .filter((r) => r !== "");

      //fetch all search terms extended ones and in main bar
      let allSearchTerms = searchTerms.concat(extendedSearchTerms);

      currentSearchTerms.map((term) => {
        if (allSearchTerms.indexOf(term.trim()) === -1) {
          if (term.trim() !== "") allSearchTerms.push(term.trim());
          updateSearchTerms(allSearchTerms);
        }
        store.dispatch({
          type: ASSET_DETAILS.SEARCH_TERMS,
          payload: allSearchTerms,
        });
      });

      setSearchValue("");

      if (event.key === "Enter" || event.key === ",") {
        // update search results based on enter press
        onSearch(allSearchTerms);
        store.dispatch({
          type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
          payload: false,
        });
      }
    }
  };

  // on cross click remove search term from field
  const removeSearchTerm = (searchTermId, isExtended) => {
    let tmpSearchTerms = isExtended ? extendedSearchTerms : searchTerms;
    // find search term based on id
    tmpSearchTerms = tmpSearchTerms.filter((s, id) => id !== searchTermId);
    tmpSearchTerms = isExtended ? searchTerms.concat(tmpSearchTerms) : tmpSearchTerms.concat(extendedSearchTerms);

    updateSearchTerms(tmpSearchTerms);

    //search on remove keywords
    setSearchValue("");
    onSearch(tmpSearchTerms);
  };

  const handleSearchFocus = (e) => {
    setShowHint(searchNode.current.contains(e.target)); // show search hint on search field click
    if (smartSearchTerms && onEdit) onEdit(searchNode.current.contains(e.target));
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleSearchFocus);
    return () => {
      document.removeEventListener("mousedown", handleSearchFocus);
    };
  }, []);

  const reloadSearch = () => {
    let allSearchTerms = searchTerms.concat(extendedSearchTerms);
    onSearch(allSearchTerms);
  };

  const resetSearch = () => {
    //clear all arrays on reset click
    setSelectedDatesObj({ start_date: "", end_date: "" });
    setSearchTerms([]);
    setExtendedSearchTerms([]);
    setSearchValue("");
    setShowReset(false);
    removeSelectedIds();
    onSearch([]);
    store.dispatch({
      type: SEARCH_ARTICLES.RESET_TOTAL_COUNT,
    });
    store.dispatch({
      type: PREVIEW_ARTICLE.UPDATE_SEARCH_TERMS,
      payload: includedKeywords,
    });
    store.dispatch({
      type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
      payload: true,
    });
    // if search box is empty then look in the advance search. If includedKeywords is empty then
    // the API call should not be sent from the frontend otherwise api responseded due to the included dates
    if (includedKeywords.length !== 0) {
      getSearchArticles(
        [],
        0,
        11,
        sortBy,
        dateFilter,
        favourites,
        true,
        sourcedContent,
        excludedContent,
        excludedKeywords,
        includedKeywords
      );
    }
  };

  useEffect(() => {
    if (searchTerms?.length === 0 && !noKeywords) {
      if (smartSearchTerms?.length > 0 && !initiallyAdded) {
        setInitiallyAdded(true);
      }
    }
  }, [smartSearchTerms]);

  const advancedSearchClickHandler = () => {
    store.dispatch({
      type: GLOBAL_ACTION.OPEN_ADVANCED_SEARCH_SIDEBAR,
    });
  };

  return (
    <>
      <Wrapper editable={editable}>
        <div className={`row`}>
          <div className={isAssetWithoutCampaign || hideUpdateButton ? "col-12" : "col-12"}>
            <InsideWrapper>
              <div ref={searchNode} className="search-wraper">
                <div ref={searchInputContainer} className="search-bar search-added">
                  <span href="#" className="refresh-search" data-qa="qa-refresh-icon">
                    <ReloadIcon onClick={reloadSearch} />
                  </span>
                  <div className="search-terms d-flex flex-nowrap">
                    <div id="search-terms-container">
                      <SearchTermsContainer
                        editable={editable}
                        searchTerms={searchTerms}
                        onRemove={removeSearchTerm}
                        isExtended={false}
                      />
                    </div>
                    {!(editable && showHint) && extendedSearchTerms.length > 0 && (
                      <SearchTermExtended count={extendedSearchTerms?.length} />
                    )}

                    <input
                      type="text"
                      name="search"
                      onChange={(e) => (e.target.value !== "," ? setSearchValue(e.target.value) : null)}
                      onKeyPress={(e) => addSearchTerm(e)}
                      className="search-input"
                      data-qa="qa-search-input"
                      placeholder={
                        searchTerms.length > 0 || extendedSearchTerms.length > 0
                          ? ""
                          : !history.location.search.includes("&asset_id=") && searchTerms?.length === 0
                          ? "Search for keywords, ideas, concepts or companies…"
                          : searchTerms.length === 0 &&
                            extendedSearchTerms.length === 0 &&
                            "Search for keywords, ideas, concepts or companies…"
                      }
                      value={searchValue}
                      autoComplete="off"
                      onFocus={(e) => setShowHint(true)}
                    />
                  </div>
                  {smartSearchTerms && showReset && editable && (
                    <span className="clear-search">
                      <SearchCross onClick={resetSearch} />
                    </span>
                  )}
                </div>
                {editable && showHint && (
                  <div className={`search-results show ${hideFocusMessage ? "showExtended" : ""}`}>
                    {!hideFocusMessage && (
                      <div className="search-hint">
                        <SearchBulb />
                        <ul>
                          <li>• Add keywords separated by commas to get more tailored content.</li>
                          <li>
                            • Add “<strong>AND</strong>” between search terms to get results containing both search
                            terms. i.e. (Sony AND Playstation)
                          </li>
                        </ul>
                      </div>
                    )}
                    {extendedSearchTerms.length > 0 && (
                      <ul className={`results-list-term ${hideFocusMessage ? "extended" : ""}`}>
                        <div className="inner-wrapper">
                          <SearchTermsContainer
                            searchTerms={extendedSearchTerms}
                            onRemove={removeSearchTerm}
                            isExtended={true}
                          />
                        </div>
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div onClick={advancedSearchClickHandler} className="advanced-search-style">
                <img src={SearchAdvance} style={{ borderLeft: "1px solid #dadada", padding: "0 5px" }} />
                Advanced Search
              </div>
            </InsideWrapper>
          </div>
        </div>
      </Wrapper>
    </>
  );
};

export default Search;

const InsideWrapper = styled.div`
  position: relative;
  .advanced-search-style {
    font-size: 16px;
    color: #7e7e7e;
    font-family: "Roboto";
    /* weight: 700; */
    margin-top: 8px;
    padding-left: 5px;
    /* border-left: 1px solid #dadada; */
    position: absolute;
    right: 18px;
    z-index: 9;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 3px;
    }
    &:hover {
      cursor: pointer;
    }
  }
`;

const Wrapper = styled.div`
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .search-wraper {
    position: relative;
    width: 100%;
    z-index: 3;
    .search-results {
      border-radius: 22px;
      position: absolute;
      background: rgb(247, 247, 247);
      top: 0px;
      left: 0;
      float: left;
      width: 100%;
      /* border: 1px solid #dadada; */
      box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.12);
      overflow: hidden;
      transition: 0.5s;
      z-index: -1;
      opacity: 0;
      padding-top: 20px;
      &.showExtended {
        top: 0px;
        /* @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          top: -65px;
        } */
      }
      &.show {
        opacity: 1;
        z-index: 2;
        visibility: visible;
        min-height: 44px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          min-height: 24px;
        }
      }
      ul.results-list {
        background: #fff;
        padding: 30px 20px 10px 55px;
        margin: 0;
        list-style: none;
        float: left;
        display: block;
        width: 100%;
        transition: 0.5s;

        li {
          color: #5e7079;
          font-size: 16px;
          line-height: 34px;
          padding: 0;
          list-style: none;
          width: 100%;
          display: flex;
          justify-content: space-between;
          cursor: pointer;
          transition: 0.5s;
          span {
            color: #c5c5c5;
            font-size: 12px;
            font-family: "robotoregular", sans-serif;
          }
        }
      }
      ul.results-list-term {
        background: #fff;
        padding: 30px 20px 10px 55px;
        margin: 0;
        border-bottom-left-radius: 20px;
        border-bottom-right-radius: 20px;
        list-style: none;
        float: left;
        display: block;
        width: 100%;
        transition: 0.5s;
        &.empity {
          padding: 20px 20px 0 55px;
        }
        .inner-wrapper {
          > div {
            margin-bottom: 4px;
          }
          max-height: 280px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #001760 #efefef;
          ::-webkit-scrollbar {
            width: 5px;
            height: 5px;
            border-radius: 2px;
          }
          /* Track */
          ::-webkit-scrollbar-track {
            background: #efefef;
            margin-top: 10px;
            margin-bottom: 15px;
          }
          /* Handle */
          ::-webkit-scrollbar-thumb {
            background: #001760;
          }
          /* Handle on hover */
          ::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        }
        li {
          color: #5e7079;
          font-size: 16px;
          line-height: 34px;
          padding: 0;
          list-style: none;
          width: 100%;
          display: flex;
          justify-content: space-between;
          cursor: pointer;
          transition: 0.5s;
          span {
            color: #c5c5c5;
            font-size: 12px;
            font-family: "robotoregular", sans-serif;
          }
        }
      }
      ul.extended {
        margin-top: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0px;
        }
      }
      .relevant-theme,
      .relevant-company {
        border-top: 1px solid #dadada;
        padding: 10px 40px 30px 40px;
        background: #f7f7f7;
        float: left;
        width: 100%;
        transition: 0.5s;
        h3 {
          margin-bottom: 10px;
          color: #424b50;
          font-weight: 400;
          font-family: "Roboto Slab", serif;
          font-size: 18px;
          line-height: 34px;
        }
      }
      .relevant-keywords {
        padding: 10px 40px 30px 40px;
        background: #f7f7f7;
        float: left;
        width: 100%;
        transition: 0.5s;
        div {
          margin-bottom: 4px;
        }
        h3 {
          margin-bottom: 10px;
          color: #424b50;
          font-weight: 400;
          font-family: "Roboto Slab", serif;
          font-size: 18px;
          line-height: 34px;
        }
      }
      .search-hint {
        background: #def6e6;
        padding: 18px 25px 36px 25px;
        border-radius: 22px 22px 0 0;
        float: left;
        width: 100%;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding: 12px 25px 28px 25px;
        }
        svg {
          float: left;
          width: 35px;
          height: 39px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 25px;
          }
        }
        ul {
          margin: 0;
          float: left;
          padding-left: 15px;
          padding-top: 5px;
          li {
            color: #09a43a;
            font-size: 14px;
            font-weight: 400;
            list-style: none;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
            }
            strong {
              font-weight: 700;
            }
          }
        }
      }
    }
  }
  .search-bar {
    border-radius: 27px;
    /* background: ${(props) => (props.editable === false ? "#F2F2F2" : "#FFFFFF")};
    border: 1px solid ${(props) => (props.editable === false ? "#F2F2F2" : "#FFFFFF")}; */
    background: #ffffff;
    border: 1px solid #ffffff;
    display: block;
    float: left;
    width: 100%;
    overflow: hidden;
    z-index: 7;
    position: relative;
    /* padding-left: ${(props) => (props.editable === false ? "15px" : "0px")}; */
    padding-left: 0px;
    box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.12);
    .search-terms {
      width: calc(100% - 250px);
      overflow: hidden;
      float: left;
      margin-bottom: 3px;
      #search-terms-container {
        display: inherit;
      }
    }
    span.clear-search {
      float: right;
      margin-top: 8px;
      margin-right: 12px;
      transition: 0.5s;
      position: absolute;
      right: 170px;
      cursor: pointer;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin: 3px 8px 3px 8px;
        float: right;
      }
      svg {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 9px;
          height: auto;
        }
      }
    }
    span.refresh-search {
      padding-right: 5px;
      border-right: 1px solid #dddddd;
      /* margin: ${(props) => (props.editable === false ? 0 : "8px")}; */
      margin: 8px;
      float: left;
      cursor: pointer;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 13px;
      }
      svg {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 14px;
          height: auto;
          vertical-align: bottom;
        }
      }
    }
    .search-input {
      float: left;
      border: none;
      outline: none;
      /* width: calc(100% - 85px); */
      width: 100%;
      display: block;
      color: #424b50;
      /* background-color: ${(props) => (props.editable === false ? "#F2F2F2" : "#FFFFFF")}; */
      background-color: #ffffff;
      font-weight: 400;
      font-size: 18px;
      margin: 6px 0px 2px 0;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px;
        margin-top: 3px;
      }
      &:hover,
      &:active,
      &:focus {
        border: none;
        outline: none;
      }
    }
  }
`;

function getTextWidth(text, font) {
  // re-use canvas object for better performance
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

function getCssStyle(element, prop) {
  return window.getComputedStyle(element, null).getPropertyValue(prop);
}

function getCanvasFontSize(el = document.body) {
  const fontWeight = getCssStyle(el, "font-weight") || "normal";
  const fontSize = getCssStyle(el, "font-size") || "16px";
  const fontFamily = getCssStyle(el, "font-family") || "Times New Roman";

  return `${fontWeight} ${fontSize} ${fontFamily}`;
}

function getNumericValue(num, base) {
  const parsed = parseInt(num, base);
  if (isNaN(parsed)) {
    return 0;
  }
  return parsed;
}
